/*
 * P3-I431: las vistas de página que el navegador manda a Application Insights.
 *
 * vue-application-insights 1.0.7 registra una vista de página por cada cambio de ruta con
 * `location.protocol + '//' + location.host + route.fullPath`. Con el router en modo hash,
 * `fullPath` es lo que va después del `#`, así que el regreso de MSAL
 * (`#/state=…&code=…&client_info=…&session_state=…`) llegaba como
 * `https://host/state=…&code=…`: el código de autorización, el state y el objeto del usuario
 * quedaban guardados en pageViews. Lo mismo con `#/login?code=…`.
 *
 * Aquí se recorta la URL a origen + ruta, sin query string y sin nada desde el primer `?`, `&`
 * o `=` de la ruta (la del path o la del hash). La vista de página se sigue mandando; sólo se
 * redacta. Las demás telemetrías (eventos, dependencias, excepciones) no se tocan, salvo la
 * métrica PageVisitTime, que el SDK arma con la URL de la página anterior.
 *
 * Mismo criterio que Base.URLTelemetria (api-services/base.js) para el usuario:contraseña@,
 * pero sin conservar los nombres de los parámetros: en una ruta los "nombres" son el valor.
 */

// Lo que separa la ruta de sus parámetros, en el path o en el hash.
const SEPARADORES = /[?&=]/;
// Un usuario:contraseña@ después del esquema (o de un // relativo al protocolo).
const CREDENCIALES = /^((?:[a-z][a-z0-9+.-]*:)?\/\/)[^/?#@]*@/i;

const TIPOS_VISTA = ["PageviewData", "PageviewPerformanceData"];
const METRICA_VISITA = "PageVisitTime";

function Recortar(Texto, Separadores) {
  const Pos = Texto.search(Separadores);
  return Pos < 0 ? Texto : Texto.substring(0, Pos);
}

/**
 * Origen + ruta + ruta del hash, sin query string ni parámetros.
 *   https://host/#/state=abc&code=… → https://host/#/state
 *   https://host/state=abc&code=…   → https://host/state
 *   https://host/?x=1#/login?code=… → https://host/#/login
 * Nunca lanza: lo que no es texto (o está vacío) regresa "".
 */
export function URLPaginaTelemetria(URL) {
  if (typeof URL !== "string" || URL === "") return "";
  try {
    const Hash = URL.indexOf("#");
    const Antes = (Hash < 0 ? URL : URL.substring(0, Hash)).replace(CREDENCIALES, "$1");
    const Ruta = Recortar(Antes, SEPARADORES);
    if (Hash < 0) return Ruta;
    return `${Ruta}#${Recortar(URL.substring(Hash + 1), SEPARADORES)}`;
  } catch (e) {
    return URLSinParametros(URL);
  }
}

// El respaldo: todo desde el primer ?, #, & o =, fuera.
function URLSinParametros(URL) {
  try {
    return Recortar(`${URL}`, /[?#&=]/);
  } catch (e) {
    return "";
  }
}

// El nombre de la vista ("(Vue App) / <route.name>") no trae parámetros hoy; si algún día
// trae una ruta cruda, se corta igual.
function NombreSinParametros(Nombre) {
  return typeof Nombre === "string" ? Recortar(Nombre, /[?&=#]/) : Nombre;
}

function Redactar(Objeto, Campo, Funcion) {
  if (Objeto != null && typeof Objeto[Campo] === "string") {
    Objeto[Campo] = Funcion(Objeto[Campo]);
  }
}

function RedactarVista(Item, Funcion) {
  const Datos = Item.baseData;
  Redactar(Datos, "uri", Funcion);
  Redactar(Datos, "refUri", Funcion);
  Redactar(Datos, "name", NombreSinParametros);
  // ai.operation.name: el SDK lo toma de ext.trace.name (location.pathname); los tags por si
  // alguien lo fija a mano.
  Redactar(Item.ext && Item.ext.trace, "name", Funcion);
  Redactar(Item.tags, "ai.operation.name", Funcion);
}

function RedactarVisita(Item, Funcion) {
  Redactar(Item.data, "PageUrl", Funcion);
  Redactar(Item.data, "PageName", NombreSinParametros);
}

function EsVista(Item) {
  return TIPOS_VISTA.indexOf(Item.baseType) >= 0;
}

function EsVisita(Item) {
  return Item.baseType === "MetricData" && Item.baseData != null && Item.baseData.name === METRICA_VISITA;
}

/**
 * Inicializador de telemetría: redacta las vistas de página (y la métrica PageVisitTime) en
 * su lugar y siempre deja pasar el elemento. Nunca lanza.
 */
export function RedactarPaginaTelemetria(Item) {
  try {
    if (Item == null) return true;
    if (EsVista(Item)) RedactarVista(Item, URLPaginaTelemetria);
    else if (EsVisita(Item)) RedactarVisita(Item, URLPaginaTelemetria);
  } catch (e) {
    try {
      if (EsVista(Item)) RedactarVista(Item, URLSinParametros);
      else if (EsVisita(Item)) RedactarVisita(Item, URLSinParametros);
    } catch (e2) {
      // Sin nada más que hacer: el elemento sale como el SDK lo armó.
    }
  }
  return true;
}

/**
 * Para `onAfterScriptLoaded` de vue-application-insights: corre justo después de
 * loadAppInsights() y antes de que el plugin enganche el router, así que ninguna vista de
 * página sale sin pasar por aquí.
 */
export function InstalarRedaccionPaginas(AppInsights) {
  if (AppInsights != null && typeof AppInsights.addTelemetryInitializer === "function") {
    AppInsights.addTelemetryInitializer(RedactarPaginaTelemetria);
  }
}

export default { URLPaginaTelemetria, RedactarPaginaTelemetria, InstalarRedaccionPaginas };
